import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../../lib/api";
import BetaHmIndicator from "../../../components/BetaHmIndicator";

/**
 * Iter-158.3 — Beta H&M project overview.
 *
 * Read-only workspace home for the Hair & Makeup department inside a
 * beta project. Shows canonical script status, shared scene/character
 * counts, and links to the character list and scene list. Intentionally
 * mirrors the legacy H&M project home layout so producers feel at
 * home; a persistent `BetaHmIndicator` reminds them this is beta.
 */
export default function BetaHMOverview() {
    const { projectId } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/beta/hm/projects/${projectId}/overview`);
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e));
            if (e?.response?.status === 404) nav("/beta");
        } finally {
            setLoading(false);
        }
    }, [projectId, nav]);

    useEffect(() => { refresh(); }, [refresh]);

    if (loading) {
        return <div className="min-h-screen bg-neutral-950 text-neutral-500 p-8 text-sm">Loading…</div>;
    }
    if (!data) return null;

    return (
        <div data-testid="beta-hm-overview" className="min-h-screen bg-neutral-950 text-neutral-100">
            <BetaHmIndicator />
            <div className="px-4 py-4 sm:px-8 sm:py-6">
                <header className="max-w-3xl mx-auto mb-6">
                    <Link
                        to={`/beta/projects/${projectId}`}
                        data-testid="beta-hm-overview-back"
                        className="tap text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200"
                    >
                        ← {data.project?.name}
                    </Link>
                    <h1 className="text-2xl sm:text-3xl font-semibold mt-2">Hair &amp; Makeup</h1>
                    <p className="text-[12px] text-neutral-500 mt-1">
                        {data.scene_count} scenes · {data.character_count} characters
                    </p>
                </header>

                <div className="max-w-3xl mx-auto space-y-4">
                    <section className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4">
                        <h2 className="text-[11px] uppercase tracking-[0.24em] text-neutral-400">Canonical script</h2>
                        {data.script ? (
                            <div className="mt-2">
                                <p data-testid="beta-hm-overview-script-title" className="text-sm text-neutral-100">
                                    {data.script.title}
                                </p>
                                {data.script.chars ? (
                                    <p className="text-[11px] text-neutral-500 mt-0.5">
                                        {data.script.chars.toLocaleString()} characters
                                    </p>
                                ) : null}
                            </div>
                        ) : (
                            <p className="mt-2 text-sm text-neutral-400">
                                No script uploaded yet. Upload one on the project screen to populate scenes and cast.
                            </p>
                        )}
                    </section>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Link
                            data-testid="beta-hm-overview-characters"
                            to={`/beta/projects/${projectId}/hm/characters`}
                            className="tap rounded-xl border border-neutral-800 bg-neutral-900/60 hover:bg-neutral-900 p-4"
                        >
                            <p className="text-[11px] uppercase tracking-[0.24em] text-neutral-400">Cast</p>
                            <p className="text-lg text-neutral-100 mt-1">{data.character_count} characters</p>
                            <p className="text-[11px] text-neutral-500 mt-1">Open character list →</p>
                        </Link>
                        <Link
                            data-testid="beta-hm-overview-scenes"
                            to={`/beta/projects/${projectId}/hm/scenes`}
                            className="tap rounded-xl border border-neutral-800 bg-neutral-900/60 hover:bg-neutral-900 p-4"
                        >
                            <p className="text-[11px] uppercase tracking-[0.24em] text-neutral-400">Scenes</p>
                            <p className="text-lg text-neutral-100 mt-1">{data.scene_count} scenes</p>
                            <p className="text-[11px] text-neutral-500 mt-1">Open scene list →</p>
                        </Link>
                    </div>

                    <p className="text-[11px] text-neutral-500 pt-2">
                        Read-only preview. Editing character notes, flags, images and looks arrives in the next
                        beta iteration.
                    </p>
                </div>
            </div>
        </div>
    );
}
